import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import classnames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { Grid, withStyles } from '@material-ui/core';
import { createStructuredSelector } from 'reselect';
import AddressSelectDisplay from 'containers/PrimoAccount/ServiceLocations/AddressSelectDisplay';
import { Container, Button, H1, Span } from 'components/Ui';
import Loader from 'components/Loader';
import { getServiceLocationRecentTransactions } from 'containers/PrimoAccount/actions';
import { getBillingSummary } from 'containers/PrimoProfile/actions';
import {
  selectBillingStatements,
  selectRecentTransactionAddresses,
  selectSelectedServiceLocation,
  selectSelectedServiceLocationId,
  selectBillingLocations,
  selectIsSubscriber,
} from 'containers/PrimoAccount/selectors';
import {
  selectBankAccounts,
  selectBillingSummary,
  selectCreditCards,
  selectIsBillingInformationFetching,
} from 'containers/PrimoProfile/selectors';
import { BillingPaymentItem } from 'components/ContentfulFields/ProductCard';
import RecentActivityDialog from 'components/Dialogs/RecentActivityDialog/Loadable';
import CurrentBalanceDialog from 'components/Dialogs/CurrentBalanceDialog/Loadable';
import PayBillDrawer from 'containers/PrimoAccount/NextDelivery/MakePayment/PayBillDrawer';
import RecentTransactionsList from 'containers/PrimoAccount/PrimoCommon/RecentTransactionsList';
import RecentActivitiesList from 'containers/PrimoAccount/PrimoCommon/RecentActivitiesList';
import OrdersHistory from 'containers/Orders/History';
import { GTM_TRACKING_TAGS } from 'utils/constants';
import { formatCurrency } from 'utils/common';
import { autopayIcon } from 'images';

import GetDelivery from 'components/Promos/GetDelivery';
import FindWater from 'components/Promos/FindWater';
import AutopaySignUpDrawer from 'containers/PrimoAccount/NextDelivery/MakePayment/AutopaySignUp';
import { AUTOPAY_SIGN_UP } from 'containers/PrimoAccount/NextDelivery/MakePayment/constants';
import { EDIT_BILLING_INFORMATION } from 'containers/PrimoProfile/BillingInformation/constants';
import EditBillingInformationDrawer from 'containers/PrimoProfile/BillingInformation/Drawer';
import { showDrawer, hideDrawer } from 'containers/DrawerManager/actions';
import Invoices from './Invoices';

import styles from './styles.scss';

const MUIStyles = (theme) => ({
  select: {
    '&:focus': {
      backgroundColor: theme.colors.white,
      borderRadius: 5,
    },
  },
});

const Layout = ({ children }) => (
  <div className={styles.wrapper}>
    <Container value="Past Orders" className={styles.container}>
      {children}
    </Container>
  </div>
);

const NonSubscriberView = () => (
  <Layout>
    <OrdersHistory />
    <GetDelivery />
    <FindWater />
  </Layout>
);

class PastOrdersView extends React.PureComponent {
  state = {
    showOnly: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    openRecentActivityDialog: false,
    openCurrentBalanceDialog: false,
    showBillDrawer: false,
    recentTransactionInspectedDeliveryLocation: null,
  };

  showBillDrawer = () => {
    this.setState({ showBillDrawer: true });
  };

  hideBillDrawer = () => {
    this.setState({ showBillDrawer: false });
  };

  showActivityDialog = () => {
    this.setState({ openRecentActivityDialog: true });
  };

  hideActivityDialog = () => {
    this.setState({ openRecentActivityDialog: false });
  };

  showBalanceDialog = () => {
    this.setState({ openCurrentBalanceDialog: true });
  };

  hideBalanceDialog = () => {
    this.setState({ openCurrentBalanceDialog: false });
  };

  handleOpenAutopay = () => this.props.showDrawer(AUTOPAY_SIGN_UP);

  handleCloseAutopay = () => this.props.hideDrawer(AUTOPAY_SIGN_UP);

  handleOpenManagePaymentDrawer = () =>
    this.props.showDrawer(EDIT_BILLING_INFORMATION);

  handleCloseManagePaymentDrawer = () =>
    this.props.hideDrawer(EDIT_BILLING_INFORMATION);

  componentDidMount() {
    if (this.props.serviceLocationId) {
      this.loadBillingSummary();
      this.props.getServiceLocationRecentTransactions({
        serviceLocationId: this.props.serviceLocationId,
        fromDate: this.state.showOnly,
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { serviceLocationId } = this.props;

    if (
      serviceLocationId &&
      serviceLocationId !== prevProps.serviceLocationId
    ) {
      this.loadBillingSummary();
    }
  }

  loadBillingSummary = () => this.props.getBillingSummary();

  handleSelectedDateFilterChange = (event) => {
    this.setState({ showOnly: event.target.value });
  };

  handleInvoiceInspectSelectedServiceLocationChange = (event) => {
    this.setState({ showOnly: event.target.value });
  };

  renderNoOrdersMessage = () => (
    <div className={classnames(styles.title, styles.noOrder)}>
      <h1>
        We do not have
        <br />a past order on file
      </h1>
      <div className={styles.subtitle}>This will be your first!</div>
      <Button text="BROWSE PRODUCTS" href="/products" />
    </div>
  );

  handleRecentTrxAddressChange = (addressId) => {
    this.props.getServiceLocationRecentTransactions({
      serviceLocationId: addressId,
      fromDate: this.state.showOnly,
    });
    this.setState({
      recentTransactionInspectedDeliveryLocation: addressId,
    });
  };

  render() {
    const {
      openRecentActivityDialog,
      openCurrentBalanceDialog,
      showBillDrawer,
    } = this.state;

    const {
      isSubscriber,
      dataIsLoading,
      classes,
      billingLocations,
      billingSummary,
      creditCards,
      bankAccounts,
    } = this.props;

    if (!isSubscriber) {
      return <NonSubscriberView />;
    }

    const nonMigratedBillingLocs = billingLocations.filter(
      (loc) => loc.billToBtbMgnStatus !== 'Migrated',
    );

    const showAutoPay =
      !creditCards.some((card) => card.autopay === true) &&
      !bankAccounts.some((acc) => acc.autopay === true);

    return (
      <Layout>
        <Loader loading={dataIsLoading} local>
          <article data-tracking={GTM_TRACKING_TAGS['Make a payment']}>
            <div className={styles.title}>
              <H1>Make a payment</H1>
            </div>
            <div>
              <BillingPaymentItem
                billingSummary={billingSummary}
                showBillDrawer={this.showBillDrawer}
                showActivityDialog={this.showActivityDialog}
                showBalanceDialog={this.showBalanceDialog}
                openManagePaymentDrawer={this.handleOpenManagePaymentDrawer}
              />
              {showAutoPay && (
                <div className={styles.autoPayContainer}>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div className={styles.autoPaySubContainer}>
                    <div>
                      <img src={autopayIcon} alt="" />
                      <Span>Sign up for easy AutoPay</Span>
                    </div>
                    <div>
                      {/* <span className={styles.signupContainer}>Sign up</span> */}
                      <Button
                        tabIndex="0"
                        className={styles.signupContainer}
                        onClick={this.handleOpenAutopay}
                        text="Sign up"
                        link
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </article>

          <article data-tracking={GTM_TRACKING_TAGS['Recent Transactions']}>
            <div className={styles.title}>
              <H1>Recent Activity</H1>
            </div>
            <div className={styles.search}>
              <ValidatorForm onSubmit={() => {}}>
                <Grid container spacing={4}>
                  <Grid item xs={12} sm={12} md={6}>
                    <AddressSelectDisplay
                      className={styles.serviceLocations}
                      addresses={this.props.recentTransactionAddresses}
                      name="inspectedDeliveryLocation"
                      handleAddressChange={(addressId) =>
                        this.handleRecentTrxAddressChange(addressId)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <p className={styles.total}>
                      Total: {formatCurrency(billingSummary.recentActivity)}
                    </p>
                  </Grid>
                </Grid>
              </ValidatorForm>
            </div>
            <RecentActivitiesList />
            <RecentTransactionsList
              isDisplayProducts
              serviceLocationAddress={
                this.state.recentTransactionInspectedDeliveryLocation
              }
            />
          </article>

          <OrdersHistory />

          <Invoices
            billingLocations={nonMigratedBillingLocs}
            classes={classes}
          />
        </Loader>
        <RecentActivityDialog
          open={openRecentActivityDialog}
          setStatus={this.hideActivityDialog}
        />
        <CurrentBalanceDialog
          open={openCurrentBalanceDialog}
          setStatus={this.hideBalanceDialog}
        />
        {showBillDrawer && (
          <PayBillDrawer open={showBillDrawer} onClose={this.hideBillDrawer} />
        )}
        <EditBillingInformationDrawer />
        <AutopaySignUpDrawer
          drawerProps={{ onClose: this.handleCloseAutopay }}
          formProps={{ onClose: this.handleCloseAutopay }}
        />
      </Layout>
    );
  }
}

PastOrdersView.propTypes = {
  dataIsLoading: PropTypes.bool.isRequired,
  getBillingSummary: PropTypes.func.isRequired,
  getServiceLocationRecentTransactions: PropTypes.func.isRequired,
  billingLocations: PropTypes.array,
  recentTransactionAddresses: PropTypes.array,
  serviceLocationId: PropTypes.string,
  classes: PropTypes.object.isRequired,
  billingSummary: PropTypes.object,
  creditCards: PropTypes.array,
  showDrawer: PropTypes.func,
  hideDrawer: PropTypes.func,
};

PastOrdersView.defaultProps = {
  serviceLocationId: null,
};

const mapStateToProps = createStructuredSelector({
  isSubscriber: selectIsSubscriber(),
  billingStatements: selectBillingStatements(),
  dataIsLoading: selectIsBillingInformationFetching(),
  serviceLocationId: selectSelectedServiceLocationId(),
  billingSummary: selectBillingSummary(),
  creditCards: selectCreditCards(),
  selectedServiceLocation: selectSelectedServiceLocation(),
  billingLocations: selectBillingLocations(),
  recentTransactionAddresses: selectRecentTransactionAddresses(),
  bankAccounts: selectBankAccounts(),
});

const mapDispatchToProps = {
  getServiceLocationRecentTransactions,
  getBillingSummary,
  showDrawer,
  hideDrawer,
};

export default compose(
  withStyles(MUIStyles, { withTheme: true }),
  connect(mapStateToProps, mapDispatchToProps),
)(PastOrdersView);
